import { useEffect, useState } from 'react';

import {
  Box,
  Button as MuiButton,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Drawer from '@/components/wrapped/Drawer';
import {
  COVER_OPTIONS,
  MAIN_COVER_OPTIONS,
  periodsOverlap,
  type CoverAssignment,
  type CoverPeriod,
} from '@/cam-admin/care-circles/LeaveCover.fixtures';

const ASSIGNMENT_COLUMNS = ['Care recipient', 'Cover'];

// Which colleague covers one circle. Defaults to the period's main cover; pick
// somebody to override just that circle. Owns its own choice, so changing one
// row leaves the others alone.
const CoverSelect = ({
  value,
  disabled,
}: {
  value: string;
  disabled?: boolean;
}) => {
  const [cover, setCover] = useState(value);
  return (
    <TextField
      select
      size='small'
      variant='outlined'
      fullWidth
      disabled={disabled}
      value={cover}
      onChange={(e) => setCover(e.target.value)}
    >
      {COVER_OPTIONS.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );
};

export type CoverFormFields = {
  start: string;
  end: string;
  mainCover: string;
  handoverNote: string;
};

/**
 * The cover drawer: schedule new cover, edit a period of your own, or read one
 * you are the cover for.
 *
 * Captures the dates, the main cover and a handover note, and blocks dates that
 * clash with cover the CAM has already booked. A new period seeds every circle
 * from the main cover; an existing one lists its circles here so a single one
 * can be handed to somebody else. That reassignment used to live inside an
 * expanding card on the tab, which meant the tab carried the whole of every
 * period at once.
 */
const ScheduleCoverDrawer = ({
  open,
  mode = 'create',
  initial,
  assignments,
  existingPeriods,
  excludeId,
  readOnly = false,
  onClose,
  onSubmit,
  onCancelPeriod,
}: {
  open: boolean;
  mode?: 'create' | 'edit';
  initial?: CoverFormFields;
  /** The period's circles, for reassigning one of them. Edit and read only. */
  assignments?: CoverAssignment[];
  existingPeriods: CoverPeriod[];
  // The period being edited, excluded from the overlap check.
  excludeId?: string;
  /**
   * Somebody else's leave, opened from the Cover tab. You can see what you are
   * holding and what they said about it; the dates and the cover are theirs.
   */
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (fields: CoverFormFields) => void;
  /** Drops the period. Your own upcoming leave only. */
  onCancelPeriod?: () => void;
}) => {
  const [starts, setStarts] = useState('');
  const [ends, setEnds] = useState('');
  const [mainCover, setMainCover] = useState('');
  const [note, setNote] = useState('');

  // Prefill (edit) or clear (create) each time the drawer opens.
  useEffect(() => {
    if (!open) return;
    setStarts(initial?.start ?? '');
    setEnds(initial?.end ?? '');
    setMainCover(initial?.mainCover ?? '');
    setNote(initial?.handoverNote ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial?.start, initial?.end]);

  const endsBeforeStart = Boolean(starts && ends && ends < starts);
  const clashes =
    Boolean(starts && ends) &&
    !endsBeforeStart &&
    periodsOverlap(starts, ends, existingPeriods, excludeId);
  const valid =
    Boolean(starts && ends && mainCover) && !endsBeforeStart && !clashes;

  const dateError = endsBeforeStart
    ? 'The end date can’t be before the start date.'
    : clashes
      ? 'You’ve already got leave booked over these dates.'
      : undefined;

  const submit = () =>
    onSubmit({ start: starts, end: ends, mainCover, handoverNote: note });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        readOnly ? 'Cover' : mode === 'edit' ? 'Edit cover' : 'Set your cover'
      }
      subtitle={
        readOnly
          ? 'Whose circles you’re holding, and what they asked you to know.'
          : 'Tell us when you’re away and who should manage your circles.'
      }
      footer={
        readOnly ? undefined : (
          <Button
            variant='primary'
            size='md'
            isFullWidth
            isDisabled={!valid}
            ariaLabel={mode === 'edit' ? 'Save' : 'Continue'}
            onPress={submit}
          >
            {mode === 'edit' ? 'Save' : 'Continue'}
          </Button>
        )
      }
    >
      <Stack spacing={3}>
        <Stack spacing={1.5}>
          <Typography variant='body1'>When are you away?</Typography>
          <Stack direction='row' spacing={2}>
            <TextField
              slotProps={{ inputLabel: { shrink: true } }}
              label='Starts'
              type='date'
              variant='filled'
              disabled={readOnly}
              value={starts}
              onChange={(e) => setStarts(e.target.value)}
              error={Boolean(dateError)}
              fullWidth
            />
            <TextField
              slotProps={{ inputLabel: { shrink: true } }}
              label='Ends'
              type='date'
              variant='filled'
              disabled={readOnly}
              value={ends}
              onChange={(e) => setEnds(e.target.value)}
              error={Boolean(dateError)}
              helperText={dateError}
              fullWidth
            />
          </Stack>
        </Stack>

        <TextField
          slotProps={{
            inputLabel: { shrink: true },
            select: {
              displayEmpty: true,
              renderValue: (v) =>
                v ? (
                  String(v)
                ) : (
                  <Typography
                    component='span'
                    variant='inherit'
                    color='text.secondary'
                  >
                    Who should receive your notifications?
                  </Typography>
                ),
            },
          }}
          select
          label='Main cover'
          variant='filled'
          disabled={readOnly}
          value={mainCover}
          onChange={(e) => setMainCover(e.target.value)}
          fullWidth
        >
          {MAIN_COVER_OPTIONS.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Handover note'
          placeholder='Anything your cover should know across the board.'
          variant='filled'
          disabled={readOnly}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />

        {/* Only when you are the cover: what you are holding, and who has
            each one. Booking your own leave stops at the main cover — a
            per-circle picker there asked you to make five decisions when one
            was enough. */}
        {readOnly && assignments && assignments.length > 0 && (
          <Stack spacing={1.5}>
            <Divider />
            <Typography variant='body1'>Circles you’re holding</Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {ASSIGNMENT_COLUMNS.map((col) => (
                    <TableCell key={col}>
                      <Typography variant='body2' color='text.secondary'>
                        {col}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((a) => (
                  <TableRow key={a.careRecipient}>
                    <TableCell>{a.careRecipient}</TableCell>
                    <TableCell width={150}>
                      <CoverSelect value={a.cover} disabled={readOnly} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        )}

        {/* Destructive, so it sits at the bottom away from Save rather than
            beside it in the footer. */}
        {onCancelPeriod && !readOnly && (
          <Stack sx={{ alignItems: 'flex-start' }}>
            <MuiButton
              variant='text'
              color='error'
              size='small'
              onClick={onCancelPeriod}
            >
              Cancel this leave
            </MuiButton>
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};

export default ScheduleCoverDrawer;
